/**
 * Generic confirmation modal. Visual-only — no business logic. Drive it via
 * `useConfirm()` (see `frontend/src/hooks/useConfirm.tsx`) so the caller gets
 * a Promise<boolean> back instead of having to wire local state in every page.
 */
import type { ReactNode } from 'react';
import * as Icons from '@components/Icons';

export type ConfirmTone = 'danger' | 'warning' | 'primary';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Visual flavour of the confirm button. */
  tone?: ConfirmTone;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'danger',
  isPending = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  if (!open) return null;

  const variant = tone === 'danger' ? 'danger' : tone === 'warning' ? 'warning' : 'primary';

  return (
    <div className="modal-scrim" onClick={isPending ? undefined : onCancel}>
      <div
        className="modal"
        style={{ maxWidth: 440 }}
        onClick={(e) => { e.stopPropagation(); }}
        role="alertdialog"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-msg"
      >
        <div className="modal-head">
          <div className="modal-title" id="confirm-title">{title}</div>
          <button
            className="icon-btn"
            data-variant="ghost"
            onClick={onCancel}
            disabled={isPending}
            aria-label="Cerrar"
          >
            <Icons.X size={14} />
          </button>
        </div>
        <div className="modal-body">
          <div id="confirm-msg" style={{ fontSize: 14, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
            {message}
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </button>
          <button
            className="btn"
            data-variant={variant}
            onClick={onConfirm}
            disabled={isPending}
            autoFocus
          >
            {isPending ? 'Procesando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
